/* eslint-disable import/no-cycle */
import api from '~/services';
import log from '~/helpers/log';
import * as lib from '~/dependencies';
import * as actions from '~/store/actions';
import { getToken } from '~/helpers';
import * as actionTypes from './actionTypes';

export const authStart = () => ({
  type: actionTypes.AUTH_START,
});

export const setEnable2FA = (userId, email, name) => ({
  type: actionTypes.TWO_FACTOR_AUTH_START,
  twoFactorInfo: {
    isEnabled: true,
    userId,
    email,
    name,
  },
});

export const setAuthUserEmail = email => ({
  type: actionTypes.SET_AUTH_USER_EMAIL,
  email,
});

export const setAuthUserPassword = password => ({
  type: actionTypes.SET_AUTH_USER_PASSWORD,
  password,
});

export const setAuthLoading = loading => ({
  type: actionTypes.SET_AUTH_LOADING,
  loading,
});

export const setNewPasswordRequirements = requirements => ({
  type: actionTypes.SET_NEW_PASSWORD_REQUIREMENTS,
  requirements,
});

export const authSetUserExtension = value => ({
  type: actionTypes.SET_USER_EXTENSION,
  payload: {
    userExtension: value,
  },
});

export const setUserCountry = userInfoResponse => async dispatch => {
  try {
    const { user_country, user_extension, time_zone } = userInfoResponse;

    const userCountry = user_country;
    const userExtension = user_extension?.toString();
    const userTimeZone = !lib._.isEmpty(time_zone)
      ? time_zone
      : 'Europe/Dublin';

    dispatch({
      type: actionTypes.SET_USER_COUNTRY,
      userCountry,
      userExtension,
      userTimeZone,
    });
  } catch (err) {
    log.error('Failed to change country code.', err?.data || err);
  }
};

export const authSuccess = (
  token,
  userId,
  userExtension,
  isAdmin = false,
  userPermissions = null
) => dispatch => {
  dispatch({
    type: actionTypes.AUTH_SUCCESS,
    token,
    userId,
    userExtension,
    isAdmin,
    userPermissions,
    loading: true,
  });
};

export const authFail = (errorCode, error = null) => ({
  type: actionTypes.AUTH_FAIL,
  errorCode,
  error,
});

export const fetchAccountInfo = () => async dispatch => {
  try {
    const [
      userInfo,
      accountInfo,
      smsBillPayRate,
      smsSenderNameList,
    ] = await Promise.all([
      api.users.get.userInfo(),
      api.accounts.get.accountInfo(),
      api.sms.get.smsBillPayRate(),
      api.sms.get.smsSenderNames(),
    ]);

    if (
      accountInfo &&
      accountInfo.data &&
      smsBillPayRate &&
      smsBillPayRate.data
    ) {
      const smsSenderNames = smsSenderNameList.data;
      const { billing, company_name, sms_sender_name } = accountInfo.data;
      const { sms_credit_rate } = smsBillPayRate.data;
      const {
        user_name,
        account_id,
        outbound_cli,
        user_extension,
        account_name,
        user_shared_accounts,
      } = userInfo.data;

      localStorage.setItem('userExtension', user_extension);

      dispatch({
        type: actionTypes.SET_ACCOUNT_INFO,
        billingType: billing.billing_type,
        smsBillingType: billing.sms_billing_type,
        accountBalance: billing.account_balance,
        smsBalance: billing.sms_balance,
        smsBillPayRate: sms_credit_rate,
        smsSenderName: sms_sender_name,
        isAdmin: userInfo.data.user_admin,
        userIdCode: userInfo.data.user_id,
        smsSenderNames,
        company_name,
        user_name,
        account_id,
        outbound_cli,
        user_extension,
        account_name,
        user_shared_accounts,
      });

      dispatch(authSetUserExtension(String(user_extension)));

      dispatch(setUserCountry(userInfo.data));

      dispatch(actions.fetchUserList());
      dispatch(actions.fetchDepartmentList());
      dispatch(actions.fetchFeatureFlags());
      dispatch(actions.setUnreadMessageCount());
      dispatch(actions.fetchMessageSenders());

      setTimeout(() => {
        dispatch(actions.getAllConversations());
      }, 2500);
    }
  } catch (err) {
    log.error('Failed to retrieve account info.', err);
  }
};

export function clearCookieToken() {
  document.cookie = 'token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
}

export const clearLocalStorage = () => {
  localStorage.removeItem('userPermissions');
  localStorage.removeItem('userExtension');
  localStorage.removeItem('isAdmin');
  localStorage.removeItem('expirationDate');
  localStorage.removeItem('token');
  localStorage.removeItem('userId');
};

export const logout = () => async dispatch => {
  clearLocalStorage();
  clearCookieToken();
  dispatch(actions.resetStates());
};

export const auth = (
  email,
  password,
  secureCode = null,
  ssoProvider = null,
  ssoSecureCode = null
) => async dispatch => {
  try {
    let login = null;

    if (ssoProvider && ssoSecureCode) {
      login = (
        await api.auth.get.authSSOSecureCode(
          ssoProvider,
          encodeURIComponent(ssoSecureCode)
        )
      ).data;
    } else if (email && password && secureCode) {
      login = (await api.users.post.authSecureCode(email, password, secureCode))
        .data;
    } else if (email && password) {
      dispatch(authStart());
      login = (await api.users.post.login(email, password)).data;
    }

    const { sessionToken, twoFaEnabled, formData, code, requirements } = login;

    if (code === 4001 && requirements) {
      dispatch(setNewPasswordRequirements(requirements));
    } else if (twoFaEnabled && formData && password) {
      const { userId, name } = formData;
      dispatch(setEnable2FA(userId, email, name));
    } else {
      const expirationDate = new Date(
        new Date().getTime() + login.expireIn * 1000
      );

      localStorage.setItem('token', sessionToken);
      localStorage.setItem('userId', email);
      localStorage.setItem('expirationDate', expirationDate);

      // Create a cookie with the token
      document.cookie = `token=${sessionToken}; expires=${expirationDate}; secure; SameSite=strict; path=/;`;
      const userInfo = await api.users.get.userInfo();

      if (userInfo && userInfo.data) {
        const {
          user_admin,
          user_extension,
          user_permissions_access,
        } = userInfo.data;

        const getUserPermissions = permissions => {
          if (permissions && permissions.call_log_access) {
            return permissions;
          }
          return null;
        };

        if (user_admin || user_extension) {
          const userExtension = user_extension;
          const isAdmin = user_admin;
          const userPermissions = getUserPermissions(user_permissions_access);
          localStorage.setItem('userExtension', userExtension);
          localStorage.setItem('isAdmin', isAdmin);
          localStorage.setItem(
            'userPermissions',
            JSON.stringify(userPermissions)
          );
          dispatch(
            authSuccess(
              sessionToken,
              email,
              userExtension,
              isAdmin,
              userPermissions
            )
          );
        } else if (!user_extension) {
          localStorage.removeItem('userPermissions');
          localStorage.removeItem('userExtension');
          localStorage.removeItem('isAdmin');
          localStorage.removeItem('expirationDate');
          localStorage.removeItem('token');
          localStorage.removeItem('userId');

          clearCookieToken();

          dispatch(authFail(4005)); // NO_EXTENSION
        }
      }
    }
  } catch (err) {
    const errorCode = err?.response?.data?.code;
    const errorMessage = err?.response?.data?.error;
    const status = err?.response?.status;
    const requirements = err?.response?.data?.requirements;

    if (status === 401) {
      if (errorCode === 4001 && requirements) {
        dispatch(setNewPasswordRequirements(requirements));
      }

      if (
        errorCode === 401 &&
        errorMessage === 'Two-factor authentication code invalid'
      ) {
        dispatch(authFail('auth-invalid-secure-code'));
      } else {
        dispatch(authFail(errorCode));
      }
    } else {
      // Fallback for default
      dispatch(authFail('auth-generic-error'));
    }
  }
};

export const cancel2FA = () => dispatch => {
  dispatch({
    type: actionTypes.TWO_FACTOR_AUTH_RESET,
    twoFactorInfo: {
      isEnabled: false,
      userId: null,
      email: null,
      name: null,
    },
  });
};

export const authCheckState = () => dispatch => {
  const token = getToken();
  // const token = localStorage.getItem('token');
  if (token === null) {
    dispatch(logout(true));
  } else {
    const expirationDate = new Date(localStorage.getItem('expirationDate'));
    if (expirationDate <= new Date()) {
      dispatch(logout(true));
    } else {
      const userId = localStorage.getItem('userId');
      const userExtension = localStorage.getItem('userExtension');
      const isAdmin = JSON.parse(localStorage.getItem('isAdmin'));
      const userPermissions = JSON.parse(
        localStorage.getItem('userPermissions')
      );
      dispatch(
        authSuccess(token, userId, userExtension, isAdmin, userPermissions)
      );
      // dispatch(
      //   // expire the session in one year
      //   checkAuthTimeout(
      //     Math.ceil((expirationDate.getTime() - new Date().getTime()) / 5)
      //   )
      // );
    }
  }
};

export const setNuacomIntegrations = integrations => async dispatch => {
  dispatch({
    type: actionTypes.SET_NUACOM_INTEGRATIONS,
    integrations,
  });
};

export const setAuthRedirectPath = path => ({
  type: actionTypes.SET_AUTH_REDIRECT_PATH,
  path,
});

export const resetAuth = keepEmail => dispatch => {
  dispatch({
    type: actionTypes.AUTH_RESET,
    payload: { keepEmail },
  });
};
